import { Badge, Button, Tabs } from 'antd';
import { useState } from 'react';
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useShallow } from 'zustand/shallow';

import { FetchingDataLoading, PageTitle } from '@/components';
import { ROUTES_PATH } from '@/constants/routes.constants';
import { OrganizationAccounts } from '@/features/organization/components/organization-accounts';
import { OrganizationBillingInformation } from '@/features/organization/components/organization-billing-information';
import { OrganizationInformation } from '@/features/organization/components/organization-information';
import { OrganizationMaterials } from '@/features/organization/components/organization-materials';
import { OrganizationShops } from '@/features/organization/components/organization-shops';
import { ORGANIZATION_CONTRACT_STATUS_CONTENTS } from '@/features/organization/constants';
import { useUpdateOrganization } from '@/features/organization/mutations/organization';
import { useOrganizationDetail } from '@/features/organization/queries/organization';
import { useFetchSaleAreas } from '@/features/organization/queries/sale-areas';
import type { OrganizationRequest } from '@/features/organization/types';
import { useModalMethod } from '@/hooks/use-modal-method';
import { useUserInformation } from '@/hooks/use-user-information';
import { useCommonStore } from '@/store/common.store';

globalThis.history.replaceState(undefined, '');

const TAB_KEYS = new Set([
  'shops',
  'accounts',
  'order',
  'information',
  'billing',
]);

export function OrganizationDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const [activeKey, setActiveKey] = useState(
    TAB_KEYS.has(searchParams.get('tab')!)
      ? searchParams.get('tab')
      : (state?.activeKey ?? 'shops'),
  );
  const { confirm } = useModalMethod();
  const { isFormChanged, setIsFormChanged } = useCommonStore(
    useShallow(({ isFormChanged, setIsFormChanged }) => ({
      isFormChanged,
      setIsFormChanged,
    })),
  );

  const { organizationId, isChainRole } = useUserInformation();
  const currentOrganizationId = id ?? organizationId!;

  const { isFetching: isFetchingSaleAreas } = useFetchSaleAreas();
  const {
    data: organizationDetail,
    isFetching: isFetchingOrganizationDetail,
    isFetchedAfterMount: isFetchedAfterMountOrganizationDetail,
  } = useOrganizationDetail(currentOrganizationId);

  const { mutate: updateOrganization } = useUpdateOrganization();

  const {
    name: organizationName,
    sale_area_ids: saleAreaIds,
    contract_status: contractStatus,
    contract_type: contractType,
    is_use_wedding_store: isWeddingChain,
    login_account_id: loginAccountId,
  } = organizationDetail ?? {};

  function handleUpdateOrganization(values: OrganizationRequest) {
    const {
      sale_area_ids: requestSaleAreaIds,
      contract_type: requestContractType,
      ...rest
    } = values;
    const requestBody = {
      ...rest,
      sale_area_ids: requestSaleAreaIds || saleAreaIds,
      contract_type: requestContractType || contractType,
    };
    updateOrganization({ id: currentOrganizationId, requestBody });
  }

  function handleTabChange(key: string) {
    if (isFormChanged) {
      confirm({
        okText: '移動する',
        title: '保存が完了していません。',
        content:
          'このタブから離れると、入力中のデータは失われます。保存せず移動しますか？',
        onOk() {
          setActiveKey(key);
          setIsFormChanged(false);
        },
      });
      return;
    }
    setActiveKey(key);
    setIsFormChanged(false);
  }

  if (
    (isFetchingOrganizationDetail && !isFetchedAfterMountOrganizationDetail) ||
    isFetchingSaleAreas
  )
    return <FetchingDataLoading />;

  return (
    <>
      <PageTitle
        title={organizationName}
        smallMargin
        rightContent={
          !isChainRole && (
            <Link to={ROUTES_PATH.organizationList}>
              <Button>戻る</Button>
            </Link>
          )
        }
      />
      <Badge
        {...ORGANIZATION_CONTRACT_STATUS_CONTENTS[contractStatus!]}
        className="ant-badge-with-background mb-16"
      />
      <Tabs
        activeKey={activeKey}
        onChange={handleTabChange}
        items={[
          {
            key: 'shops',
            label: isWeddingChain ? '店舗・企画' : '店舗',
            children: (
              <OrganizationShops
                activeKey={activeKey}
                contractType={contractType}
                isWeddingChain={isWeddingChain}
              />
            ),
          },
          {
            key: 'accounts',
            label: 'アカウント',
            children: (
              <OrganizationAccounts
                activeKey={activeKey}
                loginAccountId={loginAccountId}
                isWeddingChain={isWeddingChain}
              />
            ),
          },
          {
            key: 'order',
            label: '注文・申請',
            children: <OrganizationMaterials activeKey={activeKey} />,
          },
          {
            key: 'information',
            label: 'チェーン情報',
            destroyInactiveTabPane: true,
            children: (
              <OrganizationInformation
                information={organizationDetail}
                updateOrganization={handleUpdateOrganization}
              />
            ),
          },
          {
            key: 'billing',
            label: '請求情報',
            destroyInactiveTabPane: true,
            children: <OrganizationBillingInformation activeKey={activeKey} />,
          },
        ]}
      />
    </>
  );
}
